import { Box, Stack, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranding } from '@/theme/branding';

/**
 * The brand as it heads a screen: the customer's logo alone when one is set,
 * otherwise the Diwan mark beside the product name.
 *
 * A customer logo almost always carries its own name, so "Diwan" beside it
 * reads as a second company; the product is credited by `PoweredBy` instead.
 * The logo keeps its own proportions at the mark's height — a wide wordmark
 * squeezed into the square tile would be unreadable.
 */
export function Brand({
  size,
  maxWidth,
  titleVariant,
}: {
  size: number;
  /** Responsive, so a wide wordmark leaves room for the header's icons on a phone. */
  maxWidth: number | { xs: number; sm: number };
  titleVariant: TypographyProps['variant'];
}) {
  const { t } = useTranslation();
  const { logo, name } = useBranding();

  if (logo) {
    return (
      <Box
        component="img"
        src={logo}
        alt={name ?? ''}
        sx={{ height: size, width: 'auto', maxWidth, display: 'block', objectFit: 'contain' }}
      />
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <BrandMark size={size} />
      <Typography variant={titleVariant} noWrap sx={{ fontWeight: 700 }}>
        {t('app.name')}
      </Typography>
    </Stack>
  );
}

/**
 * The brand as it heads the app shell (DESIGN-SPEC §2): the customer's logo
 * alone when one is set, else their initial on an accent gradient in a 32px
 * rounded square, then the tenant's name with the product named quietly under it.
 *
 * With no tenant name the product's own name takes the title line, and the
 * product line is dropped rather than read "Diwan" twice.
 */
export function ShellBrand() {
  const { t } = useTranslation();
  const { logo, name } = useBranding();
  const title = name ?? t('app.name');
  const initial = title.trim().charAt(0).toUpperCase() || 'D';

  // A customer logo carries its own name, so it stands alone, as on sign-in.
  if (logo) {
    return (
      <Box
        component="img"
        src={logo}
        alt={name ?? ''}
        sx={{ height: 32, width: 'auto', maxWidth: { xs: 140, sm: 220 }, display: 'block', objectFit: 'contain' }}
      />
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
      <Box
        aria-hidden
        sx={{
          width: 32,
          height: 32,
          borderRadius: '9px',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          color: '#ffffff',
          fontWeight: 800,
          fontSize: 16,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
        }}
      >
        {initial}
      </Box>
      <Box sx={{ minWidth: 0, lineHeight: 1.15 }}>
        <Typography
          component="div"
          noWrap
          sx={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.02em', lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        {name && (
          <Typography component="div" noWrap sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', lineHeight: 1.3 }}>
            {t('app.name')}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/** The product and its maker, credited quietly wherever a customer's brand leads. */
export function PoweredBy() {
  const { t } = useTranslation();
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="p"
      sx={{ textAlign: 'center', opacity: 0.8 }}
    >
      {t('Powered by Diwan and The Alpha')}
    </Typography>
  );
}

/**
 * The Diwan mark: a pointed arch — the diwan's doorway — with a dot inside it,
 * the employee at their branch.
 *
 * Drawn inline rather than loaded from `/favicon.svg` so it inherits the
 * layout's sizing and needs no second request, and kept as the red tile in
 * every placement: the glyph alone disappears against the cream shell, and
 * the tile reads on a light header, a dark one and a print. The same artwork
 * is the favicon, the PWA icons and the mobile app icon.
 *
 * Only the product's own brand: a customer's logo is drawn by `Brand` instead.
 */
export function BrandMark({ size = 28 }: { size?: number }) {
  // Unique per instance: the mark appears more than once on some screens.
  const gradient = useId();
  return (
    <Box
      component="svg"
      viewBox="0 0 1024 1024"
      role="img"
      aria-hidden
      sx={{ width: size, height: size, flexShrink: 0, display: 'block' }}
    >
      <defs>
        <linearGradient id={gradient} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#A8281B" />
          <stop offset="1" stopColor="#7A160E" />
        </linearGradient>
      </defs>
      <rect width="1024" height="1024" rx="232" fill={`url(#${gradient})`} />
      <path
        d="M336 792V500A252 252 0 0 1 512 258A252 252 0 0 1 688 500V792"
        fill="none"
        stroke="#FFF8F0"
        strokeWidth="84"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="512" cy="600" r="62" fill="#F2B880" />
    </Box>
  );
}
